import { store } from '../../redux/store';
import axios from '../../common/axios';
import { setActiveOrgGuid } from '../../common/activeOrgStorage';
import { slice } from './slice';
import type { OrganizationMembership } from './types';

/**
 * Fetches the orgs the current user belongs to, for the org switcher
 */
export function getMemberships() {
  return async () => {
    const { dispatch, getState } = store;
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get<OrganizationMembership[]>('/organization/memberships');
      dispatch(slice.actions.getMembershipsSuccess(response.data));

      // activeOrgGuid lives in a single global localStorage key, so it can outlive
      // the session that set it — a token that simply expires without an explicit
      // logout, or a second user on a shared browser profile, would otherwise
      // inherit whatever org was last active. Reconcile it against this session's
      // actual memberships every time they're fetched, and clear it if it no
      // longer matches one.
      const activeOrgGuid = getState().organizationContext.activeOrgGuid;
      const stillAMember = response.data.some((m) => m.organizationGuid === activeOrgGuid);
      if (activeOrgGuid && !stillAMember) {
        setActiveOrgGuid(null);
        dispatch(slice.actions.setActiveOrg(null));
      }
    } catch {
      dispatch(slice.actions.hasError('Failed to fetch organization memberships'));
    }
  };
}

/**
 * Sets the active org context, persisting the choice to localStorage so the
 * axios interceptor can read it synchronously on every request.
 */
export function createOrganization(name: string) {
  return async () => {
    const { dispatch } = store;
    const response = await axios.post<OrganizationMembership>('/organization', { name });
    dispatch(slice.actions.addMembership(response.data));
    setActiveOrgGuid(response.data.organizationGuid);
    dispatch(slice.actions.setActiveOrg(response.data.organizationGuid));
    return response.data;
  };
}

export function setActiveOrganization(orgGuid: string | null) {
  return () => {
    const { dispatch } = store;
    setActiveOrgGuid(orgGuid);
    dispatch(slice.actions.setActiveOrg(orgGuid));
  };
}
